import { z } from 'zod';
import type {
  OutcropListPartsFragment,
  OutcropPartsFragment,
  UploadOutcropOverviewPageQuery,
} from '~/apollo/generated/v4/graphql';
import { GeologyType, LifecycleStatus } from '~/apollo/generated/v4/graphql';
import { ppNumberOrNull, ppStringOrNull } from '~/utils/zod';

export const outcropSchemaUnpublished = z.object({
  isBeingPublished: z.literal(false),

  name: z.string().min(1),
  regionId: z.preprocess(ppNumberOrNull, z.number().int().positive()),
  outcropType: z.preprocess(ppStringOrNull, z.string().nullable()),
  geologyType: z.array(z.nativeEnum(GeologyType)),
  dominantGeologyType: z.preprocess(
    ppStringOrNull,
    z.nativeEnum(GeologyType).nullable(),
  ),
  description: z.preprocess(ppStringOrNull, z.string().nullable()),
  introduction: z.preprocess(ppStringOrNull, z.string().nullable()),
  environments: z.preprocess(ppStringOrNull, z.string().nullable()),
  stratigraphy: z.preprocess(ppStringOrNull, z.string().nullable()),
  notablesAndAnalogues: z.preprocess(ppStringOrNull, z.string().nullable()),
  location: z.preprocess(ppStringOrNull, z.string().nullable()),
  safari: z.preprocess(ppStringOrNull, z.string().nullable()),
  lifecycleStatus: z.nativeEnum(LifecycleStatus),
  orientation: z.preprocess(ppStringOrNull, z.string().nullable()),
  depositionalDipDirection: z.preprocess(ppStringOrNull, z.string().nullable()),
  author: z.preprocess(ppStringOrNull, z.string().nullable()),
  qcCompleted: z.boolean(),
  outcropCategory: z.preprocess(ppStringOrNull, z.string().nullable()),
  keyWords: z.preprocess(ppStringOrNull, z.string().nullable()),
  diagenesisAndPetrophysicalProperties: z.preprocess(
    ppStringOrNull,
    z.string().nullable(),
  ),
  structuralGeology: z.preprocess(ppStringOrNull, z.string().nullable()),

  // Quality parameters
  structuralComplexity: z.preprocess(ppStringOrNull, z.string().nullable()),
  outcrop3dControl: z.preprocess(ppStringOrNull, z.string().nullable()),
  exposureQuality: z.preprocess(ppStringOrNull, z.string().nullable()),
  datasetScale: z.preprocess(ppStringOrNull, z.string().nullable()),

  // Basin
  basinName: z.preprocess(ppStringOrNull, z.string().nullable()),
  basinType: z.preprocess(ppStringOrNull, z.string().nullable()),
  basinClimate: z.preprocess(ppStringOrNull, z.string().nullable()),
  basinCatchmentArea: z.preprocess(ppNumberOrNull, z.number().nullable()),
  basinComments: z.preprocess(ppStringOrNull, z.string().nullable()),
  basinDescription: z.preprocess(ppStringOrNull, z.string().nullable()),
  basinTerrestrialMarine: z.preprocess(ppStringOrNull, z.string().nullable()),
  basinWaterDepth: z.preprocess(ppStringOrNull, z.string().nullable()),
  basinAccommodationRegime: z.preprocess(ppStringOrNull, z.string().nullable()),

  // Source area
  sourceAreaName: z.preprocess(ppStringOrNull, z.string().nullable()),
  sourceAreaDescription: z.preprocess(ppStringOrNull, z.string().nullable()),
  sourceAreaGeography: z.preprocess(ppStringOrNull, z.string().nullable()),
  sourceAreaStructure: z.preprocess(ppStringOrNull, z.string().nullable()),
  sourceAreaClimate: z.preprocess(ppStringOrNull, z.string().nullable()),
  sourceAreaDistanceToSourceArea: z.preprocess(
    ppNumberOrNull,
    z.number().nullable(),
  ),
  sourceAreaDistanceToSourceAreaDesc: z.preprocess(
    ppStringOrNull,
    z.string().nullable(),
  ),
  sourceAreaDistanceToShoreline: z.preprocess(
    ppNumberOrNull,
    z.number().nullable(),
  ),
  sourceAreaDistanceToShorelineDesc: z.preprocess(
    ppStringOrNull,
    z.string().nullable(),
  ),
  sourceAreaDistanceToShelfEdge: z.preprocess(
    ppNumberOrNull,
    z.number().nullable(),
  ),
  sourceAreaDistanceToShelfEdgeDesc: z.preprocess(
    ppStringOrNull,
    z.string().nullable(),
  ),
  sourceAreaComments: z.preprocess(ppStringOrNull, z.string().nullable()),
});

export const outcropSchemaPublished = outcropSchemaUnpublished.extend({
  isBeingPublished: z.literal(true),

  description: z.string().min(1),
  location: z.string().min(1),
  geologyType: z.array(z.nativeEnum(GeologyType)).min(1),
  outcropCategory: z.string().min(1),
  outcropType: z.string().min(1),

  basinName: z.string().min(1),
  basinType: z.string().min(1),
  basinClimate: z.string().min(1),

  structuralComplexity: z.string().min(1),
  outcrop3dControl: z.string().min(1),
  exposureQuality: z.string().min(1),
  datasetScale: z.string().min(1),
});

export const outcropSchema = z.discriminatedUnion('isBeingPublished', [
  outcropSchemaUnpublished,
  outcropSchemaPublished,
]);

export type OutcropFormValues = {
  /** Validation discriminator */
  isBeingPublished: boolean;

  name: string;
  regionId: string;
  outcropType: string;
  geologyType: string[];
  dominantGeologyType: string;
  description: string;
  introduction: string;
  environments: string;
  stratigraphy: string;
  notablesAndAnalogues: string;
  location: string;
  safari: string;
  lifecycleStatus: LifecycleStatus;
  qcCompleted: boolean;
  outcropCategory: string;
  keyWords: string;
  author: string;
  diagenesisAndPetrophysicalProperties: string;
  structuralGeology: string;

  orientation: string;
  depositionalDipDirection: string;

  // Quality parameters
  structuralComplexity: string;
  outcrop3dControl: string;
  exposureQuality: string;
  datasetScale: string;

  // Basin
  basinName: string;
  basinType: string;
  basinCatchmentArea: string;
  basinClimate: string;
  basinComments: string;
  basinDescription: string;
  basinTerrestrialMarine: string;
  basinWaterDepth: string;
  basinAccommodationRegime: string;

  // Source area
  sourceAreaName: string;
  sourceAreaDescription: string;
  sourceAreaGeography: string;
  sourceAreaStructure: string;
  sourceAreaClimate: string;
  sourceAreaDistanceToSourceArea: string;
  sourceAreaDistanceToSourceAreaDesc: string;
  sourceAreaDistanceToShoreline: string;
  sourceAreaDistanceToShorelineDesc: string;
  sourceAreaDistanceToShelfEdge: string;
  sourceAreaDistanceToShelfEdgeDesc: string;
  sourceAreaComments: string;
};

export function initialOutcrop(
  oc?: Partial<NonNullable<UploadOutcropOverviewPageQuery['outcropGet']>>,
): OutcropFormValues {
  return {
    isBeingPublished: oc?.lifecycleStatus
      ? oc.lifecycleStatus !== LifecycleStatus.Draft
      : false,

    name: oc?.name ?? '',
    regionId: String(oc?.regionId ?? ''),
    outcropType: oc?.outcropType ?? '',
    geologyType: oc?.geologyType ?? [],
    dominantGeologyType: oc?.dominantGeologyType ?? '',
    description: oc?.description ?? '',
    introduction: oc?.introduction ?? '',
    environments: oc?.environments ?? '',
    stratigraphy: oc?.stratigraphy ?? '',
    notablesAndAnalogues: oc?.notablesAndAnalogues ?? '',
    location: oc?.location ?? '',
    safari: oc?.safari ?? '',
    lifecycleStatus: oc?.lifecycleStatus ?? LifecycleStatus.Draft,
    orientation: oc?.orientation ?? '',
    depositionalDipDirection: oc?.depositionalDipDirection ?? '',
    author: oc?.author ?? '',
    qcCompleted: oc?.qcCompleted ?? false,
    outcropCategory: oc?.outcropCategory ?? '',
    keyWords: oc?.keyWords ?? '',
    diagenesisAndPetrophysicalProperties:
      oc?.diagenesisAndPetrophysicalProperties ?? '',
    structuralGeology: oc?.structuralGeology ?? '',

    // Quality parameters
    structuralComplexity: oc?.structuralComplexity ?? '',
    exposureQuality: oc?.exposureQuality ?? '',
    datasetScale: oc?.datasetScale ?? '',
    outcrop3dControl: oc?.outcrop3dControl ?? '',

    // Basin
    basinType: oc?.basinType ?? '',
    basinCatchmentArea: String(oc?.basinCatchmentArea ?? ''),
    basinClimate: oc?.basinClimate ?? '',
    basinComments: oc?.basinComments ?? '',
    basinDescription: oc?.basinDescription ?? '',
    basinName: oc?.basinName ?? '',
    basinTerrestrialMarine: oc?.basinTerrestrialMarine ?? '',
    basinWaterDepth: oc?.basinWaterDepth ?? '',
    basinAccommodationRegime: oc?.basinAccommodationRegime ?? '',

    // Source area
    sourceAreaName: oc?.sourceAreaName ?? '',
    sourceAreaDescription: oc?.sourceAreaDescription ?? '',
    sourceAreaGeography: oc?.sourceAreaGeography ?? '',
    sourceAreaStructure: oc?.sourceAreaStructure ?? '',
    sourceAreaClimate: oc?.sourceAreaClimate ?? '',
    sourceAreaDistanceToSourceArea: String(
      oc?.sourceAreaDistanceToSourceArea ?? '',
    ),
    sourceAreaDistanceToSourceAreaDesc:
      oc?.sourceAreaDistanceToSourceAreaDesc ?? '',
    sourceAreaDistanceToShoreline: String(
      oc?.sourceAreaDistanceToShoreline ?? '',
    ),
    sourceAreaDistanceToShorelineDesc:
      oc?.sourceAreaDistanceToShorelineDesc ?? '',
    sourceAreaDistanceToShelfEdge: String(
      oc?.sourceAreaDistanceToShelfEdge ?? '',
    ),
    sourceAreaDistanceToShelfEdgeDesc:
      oc?.sourceAreaDistanceToShelfEdgeDesc ?? '',
    sourceAreaComments: oc?.sourceAreaComments ?? '',
  };
}

/** Get the label of the given HTML field based on the outcrop's outcropCategory */
export function fieldLabel(
  field: keyof OutcropPartsFragment,
  ocCategory: string | null = 'outcrop',
) {
  if (field === 'description') {
    return 'Description';
  }
  if (field === 'introduction') {
    return 'Introduction';
  }

  if (field === 'environments') {
    switch (ocCategory) {
      case 'modern':
        return 'Sedimentology and Depositional Environment';
      default:
        return 'Lithology and Depositional Environment';
    }
  }

  if (field === 'stratigraphy') {
    switch (ocCategory) {
      case 'production':
        return 'Structural Complexity';
      default:
        return 'Litho- and Sequence Stratigraphy';
    }
  }

  if (field === 'location') {
    switch (ocCategory) {
      case 'seismic':
        return 'Dataset';
      case 'production':
        return 'Reservoir Engineering Aspects';
      default:
        return 'Location and Accessibility';
    }
  }

  if (field === 'notablesAndAnalogues') {
    switch (ocCategory) {
      case 'production':
        return 'Production Aspects';
      default:
        return 'Analogues and Notable Aspects';
    }
  }

  if (field === 'safari') {
    switch (ocCategory) {
      case 'production':
        return 'Key References and Links';
      default:
        return 'Key References';
    }
  }

  if (field === 'keyWords') return 'Keywords';

  if (field === 'diagenesisAndPetrophysicalProperties')
    return 'Diagenesis and Petrophysical Properties';

  if (field === 'structuralGeology') return 'Structural Geology';

  throw new Error(`Field ${field} does not have a defined label`);
}

export type SortableOutcropV4 = OutcropListPartsFragment & {
  numStudies: number;
  hasPaleoMaps: boolean;
  numVoms: number;
};

export function toSortableOutcropV4(
  outcrop: OutcropListPartsFragment,
): SortableOutcropV4 {
  return {
    ...outcrop,
    numStudies: outcrop.studies.length,
    numVoms: outcrop.virtualOutcropModels.length,
    hasPaleoMaps: Boolean(outcrop.paleoMaps.length),
  };
}

export type OCAdditionalFields = Pick<
  OutcropPartsFragment,
  | 'channelMorphology'
  | 'diageneticGeometry'
  | 'diageneticProcess'
  | 'diageneticSetting'
  | 'dominantLithology'
  | 'duneShape'
  | 'faultRocksMembranes'
  | 'interactionNetwork'
  | 'multipleFolds'
  | 'netToGross'
  | 'reactivation1stPhase'
  | 'reactivation2ndPhase'
  | 'riverProfileLocation'
  | 'secondaryStructures'
  | 'shorelineTractory'
  | 'symmetryGeometry'
  | 'synSedimentaryDeformation'
  | 'systemsTract'
  | 'tectonicSetting'
  | 'waterTemperature'
  | 'lateralAggregation'
>;
export type OCAddtlField = keyof OCAdditionalFields;

export type OCAddtlFieldConfig = {
  multiple: boolean;
  label?: string;
  wikiId?: number;
};

export const additionalFieldsConfig: Record<OCAddtlField, OCAddtlFieldConfig> =
  {
    netToGross: { multiple: false, wikiId: 71 },
    systemsTract: { multiple: true, wikiId: 39 },
    shorelineTractory: { multiple: true, wikiId: 37 },
    duneShape: { multiple: true, wikiId: 30 },
    channelMorphology: { multiple: true, wikiId: 25 },
    riverProfileLocation: { multiple: true, wikiId: 35 },
    dominantLithology: { multiple: true, wikiId: undefined },
    lateralAggregation: { multiple: true, wikiId: 82 },
    waterTemperature: { multiple: true, label: 'Water temp.', wikiId: 70 },
    diageneticProcess: { multiple: true, wikiId: 74 },
    diageneticSetting: { multiple: true, wikiId: 72 },
    diageneticGeometry: { multiple: true, wikiId: 73 },
    tectonicSetting: { multiple: true, wikiId: 72 },
    synSedimentaryDeformation: {
      multiple: true,
      label: 'Syn-sedimentary Deformation',
      wikiId: 73,
    },
    faultRocksMembranes: {
      multiple: true,
      label: 'Fault Rocks/Membranes',
      wikiId: 74,
    },
    interactionNetwork: {
      multiple: true,
      label: 'Interaction/Network',
      wikiId: 75,
    },
    reactivation1stPhase: {
      multiple: true,
      label: 'Reactivation 1st Phase',
      wikiId: 76,
    },
    reactivation2ndPhase: {
      multiple: true,
      label: 'Reactivation 2nd Phase',
      wikiId: 76,
    },
    symmetryGeometry: {
      multiple: true,
      label: 'Symmetry/Geometry',
      wikiId: 77,
    },
    multipleFolds: {
      multiple: true,
      wikiId: 78,
    },
    secondaryStructures: {
      multiple: true,
      label: 'Secondary Structures Related To Folds',
      wikiId: 79,
    },
  };

export function additionalClasticFields(
  entity: 'outcrop' | 'ae',
): OCAddtlField[] {
  const fields: OCAddtlField[] = [];

  if (entity === 'outcrop') {
    fields.push('netToGross');
  }

  const baseFields: OCAddtlField[] = [
    'systemsTract',
    'shorelineTractory',
    'duneShape',
    'channelMorphology',
    'riverProfileLocation',
    'dominantLithology',
    'lateralAggregation',
  ];
  baseFields.forEach(f => fields.push(f));

  return fields;
}

export function additionalCarbonateFields(): OCAddtlField[] {
  return [
    'waterTemperature',
    'diageneticProcess',
    'diageneticSetting',
    'diageneticGeometry',
    'systemsTract',
    'dominantLithology',
  ];
}

export type KPFields = {
  grossDepositionalEnvironment?: string | null;
  depositionalEnvironment?: string | null;
  depositionalSubEnvironment?: string | null;
  architecturalElement?: string | null;
};

export function additionalStructuralFields(kps: KPFields[]): OCAddtlField[] {
  const gdes = kps.map(kp => kp.grossDepositionalEnvironment);
  const hasClassification = (c: string) => gdes.find(gde => gde === c);

  const enumFields: OCAddtlField[] = [
    'tectonicSetting',
    'synSedimentaryDeformation',
    'dominantLithology',
  ];

  const appendFields = (fieldsToAppend: OCAddtlField[]) =>
    fieldsToAppend.forEach(f => enumFields.push(f));

  if (hasClassification('Faults/Fractures')) {
    appendFields([
      'faultRocksMembranes',
      'interactionNetwork',
      'reactivation1stPhase',
      'reactivation2ndPhase',
    ]);
  }

  if (hasClassification('Fold') || hasClassification('Fault-related fold')) {
    appendFields(['symmetryGeometry', 'multipleFolds', 'secondaryStructures']);
  }

  return enumFields;
}
